import type { AssemblyOutcome } from '../../api/schema'

export const MAJORITY_RULES = ['art24', 'art25', 'art25_1', 'art26', 'art26_1', 'unanimity', 'none'] as const
export const RESOLUTION_SUBJECTS = [
    'bureau_election', 'works', 'accounts_approval', 'budget', 'works_fund', 'syndic_mandate',
    'council_election', 'council_thresholds', 'remote_participation_means', 'information', 'other',
] as const
export const VOTES = ['for', 'against', 'abstain', 'defaulting'] as const
export const DEFAULT_REASONS = [
    'absent_unrepresented', 'mail_vote_on_amended_text', 'mail_vote_received_late', 'mail_vote_void',
] as const
export const RECORDED_OUTCOMES = ['adopted', 'rejected', 'postponed', 'not_voted'] as const
export const PRESENCES = ['present', 'represented', 'mail_vote', 'absent'] as const

export type Vote = (typeof VOTES)[number]
export type DefaultReason = (typeof DEFAULT_REASONS)[number]

/** One row of the vote-entry table: what the secretary has chosen for a co-owner. */
export interface VoteDraft {
    vote: Vote | ''
    defaultReason: string
}

/**
 * The votes to send: only the rows the user filled in. A defaulting row
 * without its cause is reported in `incomplete` instead of being sent, since
 * the server refuses it.
 */
export function buildVotePayload(drafts: Record<string, VoteDraft>): {
    votes: { user_uid: string; vote: Vote; default_reason?: DefaultReason }[]
    incomplete: string[]
} {
    const votes: { user_uid: string; vote: Vote; default_reason?: DefaultReason }[] = []
    const incomplete: string[] = []
    for (const [userUid, d] of Object.entries(drafts)) {
        if (!d.vote) continue
        if (d.vote === 'defaulting') {
            if (!d.defaultReason) {
                incomplete.push(userUid)
                continue
            }
            votes.push({ user_uid: userUid, vote: d.vote, default_reason: d.defaultReason as DefaultReason })
        } else {
            votes.push({ user_uid: userUid, vote: d.vote })
        }
    }
    return { votes, incomplete }
}

/** "350 / 600 (needs 301)": the engine's detail on one line. */
export function describeComputed(c: NonNullable<AssemblyOutcome['computed']>): string {
    if (c.result === 'none') return ''
    return `${c.numerator} / ${c.denominator} (≥ ${c.required})`
}

/** The members leg of an article 26 vote, empty for every other rule. */
export function describeMembers(c: NonNullable<AssemblyOutcome['computed']>): string {
    if (!c.members_total) return ''
    return `${c.members_for ?? 0} / ${c.members_total} (≥ ${c.members_required ?? 0})`
}

/** Does recording `recorded` over this outcome need an override reason? */
export function needsOverrideReason(outcome: AssemblyOutcome | undefined, recorded: string): boolean {
    const result = outcome?.computed?.result
    if (!result || (result !== 'adopted' && result !== 'rejected')) return false
    return recorded !== '' && recorded !== result
}

/** Badge tone of an outcome value. */
export function outcomeTone(value: string | undefined): 'success' | 'destructive' | 'secondary' {
    if (value === 'adopted') return 'success'
    if (value === 'rejected') return 'destructive'
    return 'secondary'
}

/** Moves an item one step up or down; returns the same array when it cannot move. */
export function moveItem<T>(items: T[], index: number, delta: -1 | 1): T[] {
    const target = index + delta
    if (index < 0 || index >= items.length || target < 0 || target >= items.length) return items
    const next = items.slice()
    ;[next[index], next[target]] = [next[target], next[index]]
    return next
}

/** A clause value read as text ("" when absent or not a string). */
export function clauseText(clauses: Record<string, unknown> | undefined, code: string): string {
    const v = clauses?.[code]
    return typeof v === 'string' ? v : ''
}

/** Longest resolution title (mirrors internal/assembly MaxTitleLen). */
export const MAX_TITLE_LENGTH = 100

const ABBREVIATIONS = new Set(['art', 'n°', 'no', 'al', 'cf', 'etc', 'm', 'mme', 'mmes', 'mm', 'st', 'ste', 'dr'])

/**
 * The first sentence of a legal text, cut at `max` characters on a word
 * boundary: the fallback title of a resolution that predates `resolution_title`.
 * An "art." dot does not end the sentence.
 */
export function firstSentence(text: string | undefined | null, max = MAX_TITLE_LENGTH): string {
    const t = (text ?? '').replace(/\s+/g, ' ').trim()
    if (!t) return ''
    let end = t.length
    for (let i = 0; i < t.length; i++) {
        const c = t[i]
        if (!'.!?'.includes(c)) continue
        if (i + 1 < t.length && t[i + 1] !== ' ') continue
        if (c === '.') {
            const word = t.slice(0, i).split(' ').pop()!.toLowerCase().replace(/^.*['’]/, '')
            if (ABBREVIATIONS.has(word)) continue
        }
        end = i
        break
    }
    const sentence = t.slice(0, end).replace(/[ .;:]+$/, '')
    if ([...sentence].length <= max) return sentence
    const cut = [...sentence].slice(0, max - 1).join('')
    const space = cut.lastIndexOf(' ')
    return (space > max / 2 ? cut.slice(0, space) : cut).replace(/[ ,;:.-]+$/, '') + '…'
}

interface TitledResolution {
    title?: string | null
    name?: string | null
    code?: string
    clauses?: Record<string, unknown>
}

/** What a resolution is called on screen: its title, else the first sentence of its text. */
export function resolutionTitle(c: TitledResolution): string {
    return (
        c.title?.trim() ||
        clauseText(c.clauses, 'resolution_title').trim() ||
        firstSentence(clauseText(c.clauses, 'resolution_text')) ||
        (c.name && c.name !== c.code ? c.name : '') ||
        c.code ||
        ''
    )
}

/** "art24" → "art. 24": the majority chip. Empty for a rule that carries no majority. */
export function ruleShort(rule: string | undefined): string {
    if (!rule || rule === 'none') return ''
    if (rule === 'unanimity') return 'unanimity'
    const m = /^art(\d+)(?:_(\d+))?$/.exec(rule)
    return m ? `art. ${m[1]}${m[2] ? `-${m[2]}` : ''}` : rule
}

/**
 * The widths (percent of the expressed + abstaining voices) of the for / against
 * / abstain segments of a vote bar. No voice at all gives three zeros, never NaN.
 */
export function voteShares(
    voicesFor: number | undefined | null,
    voicesAgainst: number | undefined | null,
    voicesAbstain: number | undefined | null,
): { for: number; against: number; abstain: number; total: number } {
    const f = Math.max(0, voicesFor ?? 0)
    const a = Math.max(0, voicesAgainst ?? 0)
    const b = Math.max(0, voicesAbstain ?? 0)
    const total = f + a + b
    if (total === 0) return { for: 0, against: 0, abstain: 0, total: 0 }
    return { for: (f / total) * 100, against: (a / total) * 100, abstain: (b / total) * 100, total }
}

/** The recorded outcome of a resolution row, "" when none. */
export function recordedOutcomeOf(clauses: Record<string, unknown> | undefined): string {
    return clauseText(clauses, 'recorded_outcome')
}

export const OUTCOME_FILTERS = ['adopted', 'rejected', 'not_voted'] as const
export type OutcomeFilter = (typeof OUTCOME_FILTERS)[number] | ''

/** Folds case and accents so "ravalement" finds "Ravalément". */
export function fold(s: string): string {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

interface FilterableResolution extends TitledResolution {
    clauses?: Record<string, unknown>
}

/**
 * Filters the resolutions of one PV by words (every word must appear in the
 * title or the text) and by outcome. `not_voted` also matches a postponed or
 * unrecorded resolution.
 */
export function filterResolutions<T extends FilterableResolution>(items: T[], q: string, outcome: OutcomeFilter | string): T[] {
    const words = fold(q).split(/\s+/).filter(Boolean)
    return items.filter((c) => {
        if (outcome) {
            const recorded = recordedOutcomeOf(c.clauses)
            const effective = recorded === 'adopted' || recorded === 'rejected' ? recorded : 'not_voted'
            if (effective !== outcome) return false
        }
        if (words.length === 0) return true
        const hay = fold(`${resolutionTitle(c)} ${clauseText(c.clauses, 'resolution_text')}`)
        return words.every((w) => hay.includes(w))
    })
}

/** Counts shown in the assembly header. */
export function summarizeResolutions(items: { clauses?: Record<string, unknown> }[]): {
    total: number
    adopted: number
    rejected: number
    open: number
} {
    let adopted = 0
    let rejected = 0
    for (const c of items) {
        const r = recordedOutcomeOf(c.clauses)
        if (r === 'adopted') adopted++
        else if (r === 'rejected') rejected++
    }
    return { total: items.length, adopted, rejected, open: items.length - adopted - rejected }
}

/** Badge tone of a decision's standing. */
export function forceTone(force: string | undefined): 'success' | 'warning' | 'destructive' | 'secondary' | 'info' {
    switch (force) {
        case 'in_force':
            return 'success'
        case 'contested':
        case 'pending_validation':
            return 'warning'
        case 'annulled':
            return 'destructive'
        case 'superseded':
            return 'info'
        default:
            return 'secondary'
    }
}

/** A step of a stepper: done / to do / blocked. */
export type StepStatus = 'done' | 'todo' | 'blocked'

/** The step to open by default: the first one that is not done and not blocked, else the first not done, else the last. */
export function currentStep(steps: { id: string; status: StepStatus }[]): string | undefined {
    return (
        steps.find((s) => s.status === 'todo')?.id ??
        steps.find((s) => s.status === 'blocked')?.id ??
        steps[steps.length - 1]?.id
    )
}
